import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { SignIn, SignOut, User } from "@phosphor-icons/react";

export default function Attendance() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [shift, setShift] = useState("Day");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    const { data: r } = await api.get(`/attendance?date=${date}`);
    setRows(r);
    const { data: s } = await api.get("/attendance/summary");
    setSummary(s);
  };
  useEffect(() => { load(); }, [date]);

  const punch = async (action) => {
    try {
      await api.post("/attendance", { shift, action });
      toast.success(action === "check_in" ? "Checked in" : "Checked out");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="attendance-page">
      <PageHeader subtitle="Workforce" title="Attendance & Shifts"
        actions={
          <div className="flex items-center gap-2">
            <Select value={shift} onValueChange={setShift}>
              <SelectTrigger className="w-48" data-testid="shift-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="Day">Day Shift (6 AM – 6 PM)</SelectItem>
                <SelectItem value="Night">Night Shift (6 PM – 6 AM)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={()=>punch("check_in")} data-testid="check-in-btn"><SignIn size={16} className="mr-1.5"/>Check In</Button>
            <Button onClick={()=>punch("check_out")} data-testid="check-out-btn"><SignOut size={16} className="mr-1.5"/>Check Out</Button>
          </div>
        }
      />

      <Card className="shadow-none mb-4">
        <CardContent className="p-5">
          <div className="overline mb-3">Today · Live Roster</div>
          {summary.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">No check-ins yet</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {summary.map((u, i) => (
                <div key={i} className="border border-border rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold">{u.name?.[0]}</div>
                    <div>
                      <div className="text-sm font-semibold">{u.name}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{u.shift}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div><div className="overline">In</div><div className="mono">{u.check_in ? new Date(u.check_in).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"}) : "—"}</div></div>
                    <div><div className="overline">Out</div><div className="mono">{u.check_out ? new Date(u.check_out).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"}) : "—"}</div></div>
                  </div>
                  {u.check_in && !u.check_out ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 mt-2">Present</Badge>
                    : u.check_out ? <Badge variant="secondary" className="mt-2">Signed off</Badge> : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Attendance Log</div>
            <Input type="date" value={date} onChange={e=>setDate(e.target.value)} className="w-40" data-testid="date-filter"/>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Time</TableHead><TableHead>Name</TableHead>
              <TableHead>Shift</TableHead><TableHead>Action</TableHead>
              <TableHead>Note</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="mono text-xs">{new Date(r.at).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit", second:"2-digit"})}</TableCell>
                  <TableCell className="font-semibold">{r.name}</TableCell>
                  <TableCell><Badge variant="secondary">{r.shift}</Badge></TableCell>
                  <TableCell>{r.action === "check_in"
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Check In</Badge>
                    : <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Check Out</Badge>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.note || "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No entries for this date</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
